"use client";

import createGlobe from "cobe";
import Image from "next/image";
import {
	type CSSProperties,
	memo,
	useCallback,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
} from "react";
import {
	type FlightArc,
	type FlightMarker,
	GLOBE_FLIGHTS_DEFAULT_ARCS,
	GLOBE_FLIGHTS_DEFAULT_MARKERS,
} from "./globe-flights";
import { GLOBE_LABELS_DEFAULT_MARKERS, type LabelMarker } from "./globe-labels";
import {
	GLOBE_POLAROIDS_DEFAULT_MARKERS,
	type PolaroidMarker,
} from "./globe-polaroids";
import {
	GLOBE_STICKERS_DEFAULT_MARKERS,
	type StickerMarker,
} from "./globe-stickers";
import {
	GLOBE_WEATHER_DEFAULT_MARKERS,
	type WeatherMarker,
} from "./globe-weather";

function prefixIds<T extends { id: string }>(
	prefix: string,
	items: readonly T[],
): T[] {
	return items.map((item) => ({ ...item, id: `${prefix}${item.id}` }));
}

export type GlobeMultiMarker =
	| ({ kind: "weather" } & WeatherMarker)
	| ({ kind: "flightPort" } & FlightMarker)
	| ({ kind: "polaroid" } & PolaroidMarker)
	| ({ kind: "sticker" } & StickerMarker & { rotateDeg: number })
	| ({ kind: "label" } & LabelMarker);

function buildDefaultMultiMarkers(): GlobeMultiMarker[] {
	const stickerRotations = [-8, 6, -4, 10];
	let stickerN = 0;
	const stickers = prefixIds("st-", GLOBE_STICKERS_DEFAULT_MARKERS).map(
		(m) => ({
			kind: "sticker" as const,
			...m,
			rotateDeg: stickerRotations[stickerN++ % 4],
		}),
	);
	return [
		...prefixIds("wx-", GLOBE_WEATHER_DEFAULT_MARKERS).map((m) => ({
			kind: "weather" as const,
			...m,
		})),
		...prefixIds("fp-", GLOBE_FLIGHTS_DEFAULT_MARKERS).map((m) => ({
			kind: "flightPort" as const,
			...m,
		})),
		...prefixIds("pol-", GLOBE_POLAROIDS_DEFAULT_MARKERS).map((m) => ({
			kind: "polaroid" as const,
			...m,
		})),
		...stickers,
		...prefixIds("lb-", GLOBE_LABELS_DEFAULT_MARKERS).map((m) => ({
			kind: "label" as const,
			...m,
		})),
	];
}

export const GLOBE_MULTI_DEFAULT_MARKERS: GlobeMultiMarker[] =
	buildDefaultMultiMarkers();

export const GLOBE_MULTI_DEFAULT_ARCS: FlightArc[] = prefixIds(
	"fa-",
	GLOBE_FLIGHTS_DEFAULT_ARCS,
);

function cobeMarkerSize(kind: GlobeMultiMarker["kind"]): number {
	switch (kind) {
		case "weather":
			return 0.025;
		case "flightPort":
			return 0.02;
		case "polaroid":
			return 0.02;
		case "sticker":
			return 0.03;
		case "label":
			return 0.025;
	}
}

export type GlobeMultiQuality = "auto" | "high" | "medium" | "low";

type GlobeTier = "high" | "medium" | "low";

const TIER_ORDER: Record<GlobeTier, number> = { low: 0, medium: 1, high: 2 };

function minTier(a: GlobeTier, b: GlobeTier): GlobeTier {
	return TIER_ORDER[a] <= TIER_ORDER[b] ? a : b;
}

function isSaveDataConnection(): boolean {
	if (typeof navigator === "undefined") return false;
	const conn = (
		navigator as Navigator & {
			connection?: { saveData?: boolean; effectiveType?: string };
		}
	).connection;
	if (!conn) return false;
	if (conn.saveData) return true;
	return conn.effectiveType === "slow-2g" || conn.effectiveType === "2g";
}

function isCoarsePointerDevice(): boolean {
	if (typeof window === "undefined") return false;
	return (
		window.matchMedia("(pointer: coarse)").matches ||
		(navigator.maxTouchPoints ?? 0) > 0
	);
}

function viewportTierCap(): GlobeTier {
	if (typeof window === "undefined") return "high";
	const w = window.innerWidth;
	if (w < 480) return "low";
	if (w < 900) return "medium";
	return "high";
}

function autoQualityTier(): GlobeTier {
	if (typeof window === "undefined") return "medium";
	const nav = navigator as Navigator & { deviceMemory?: number };
	const cores = nav.hardwareConcurrency ?? 4;
	const mem = nav.deviceMemory;

	let tier: GlobeTier = "high";
	if (cores <= 2 || (mem !== undefined && mem < 2)) tier = "low";
	else if (cores <= 4 || (mem !== undefined && mem < 4)) tier = "medium";

	if (isSaveDataConnection()) tier = "low";
	if (isCoarsePointerDevice()) tier = minTier(tier, "medium");

	return minTier(tier, viewportTierCap());
}

function resolveTier(quality: GlobeMultiQuality): GlobeTier {
	const base = quality === "auto" ? autoQualityTier() : quality;
	return minTier(base, viewportTierCap());
}

function thinMarkers(
	markers: GlobeMultiMarker[],
	tier: GlobeTier,
): GlobeMultiMarker[] {
	if (tier === "high") return markers;

	const caps =
		tier === "medium"
			? { weather: 8, sticker: 8, label: 6, polaroid: 4 }
			: { weather: 5, sticker: 5, label: 4, polaroid: 2 };

	const n = { weather: 0, sticker: 0, label: 0, polaroid: 0 };

	return markers.filter((m) => {
		if (m.kind === "flightPort") return true;
		if (m.kind === "weather") return n.weather++ < caps.weather;
		if (m.kind === "sticker") return n.sticker++ < caps.sticker;
		if (m.kind === "label") return n.label++ < caps.label;
		if (m.kind === "polaroid") return n.polaroid++ < caps.polaroid;
		return true;
	});
}

function thinArcs(arcs: FlightArc[], tier: GlobeTier): FlightArc[] {
	if (tier === "high") return arcs;
	if (tier === "medium") return arcs.slice(0, 4);
	return arcs.slice(0, 2);
}

function cobeConfigForTier(tier: GlobeTier): {
	mapSamples: number;
	maxDpr: number;
	frameMs: number;
} {
	switch (tier) {
		case "high":
			return { mapSamples: 12000, maxDpr: 1.75, frameMs: 1000 / 60 };
		case "medium":
			return { mapSamples: 7500, maxDpr: 1.25, frameMs: 1000 / 45 };
		case "low":
			return { mapSamples: 4500, maxDpr: 1, frameMs: 1000 / 30 };
	}
}

function fadeBlurStyle(id: string, enabled: boolean): CSSProperties {
	if (!enabled) {
		return {
			opacity: `var(--cobe-visible-${id}, 0)`,
			transition: "opacity 0.25s ease",
		};
	}
	return {
		opacity: `var(--cobe-visible-${id}, 0)`,
		filter: `blur(calc((1 - var(--cobe-visible-${id}, 0)) * 6px))`,
		transition: "opacity 0.25s ease",
	};
}

type OverlayTierProps = {
	tier: GlobeTier;
	reduceMotion: boolean;
	filterId: string;
};

const GlobeMultiArcOverlays = memo(function GlobeMultiArcOverlays({
	arcs,
	tier,
	filterId,
}: { arcs: FlightArc[] } & OverlayTierProps) {
	const arcFilter =
		tier === "high"
			? `url(#${filterId}) drop-shadow(0 1px 2px rgba(0,0,0,0.3))`
			: "drop-shadow(0 1px 2px rgba(0,0,0,0.22))";

	return (
		<>
			{arcs.map((a) => (
				<div
					key={a.id}
					style={{
						position: "absolute",
						positionAnchor: `--cobe-arc-${a.id}`,
						bottom: "anchor(top)",
						left: "anchor(center)",
						translate: "-50% 0",
						fontSize: "1.2rem",
						pointerEvents: "none",
						filter: arcFilter,
						opacity: `var(--cobe-visible-arc-${a.id}, 0)`,
						transition: "opacity 0.25s ease",
					}}
				>
					✈️
				</div>
			))}
		</>
	);
});

const GlobeMultiMarkerOverlays = memo(function GlobeMultiMarkerOverlays({
	markers,
	tier,
	reduceMotion,
	filterId,
}: { markers: GlobeMultiMarker[] } & OverlayTierProps) {
	const useFadeBlur = tier === "high";
	const polaroidSize = tier === "low" ? 48 : 60;

	const weatherFilter =
		tier === "high"
			? `url(#${filterId}) drop-shadow(0 2px 6px rgba(100,150,220,0.4))`
			: "drop-shadow(0 2px 4px rgba(100,150,220,0.3))";

	const stickerFilter =
		tier === "high"
			? `url(#${filterId}) drop-shadow(0 2px 3px rgba(0,0,0,0.3))`
			: "drop-shadow(0 2px 3px rgba(0,0,0,0.22))";

	return (
		<>
			{markers.map((m) => {
				if (m.kind === "weather") {
					return (
						<div
							key={m.id}
							style={{
								position: "absolute",
								positionAnchor: `--cobe-${m.id}`,
								bottom: "anchor(top)",
								left: "anchor(center)",
								translate: "-50% 0",
								fontSize: tier === "low" ? "1.5rem" : "1.8rem",
								filter: weatherFilter,
								pointerEvents: "none",
								opacity: `var(--cobe-visible-${m.id}, 0)`,
								transition: "opacity 0.25s ease",
								...(reduceMotion
									? {}
									: {
											animation:
												"globe-multi-weather-float 3s ease-in-out infinite",
										}),
							}}
						>
							{m.emoji}
						</div>
					);
				}
				if (m.kind === "flightPort") return null;
				if (m.kind === "polaroid") {
					return (
						<div
							key={m.id}
							style={{
								position: "absolute",
								positionAnchor: `--cobe-${m.id}`,
								bottom: "anchor(top)",
								left: "anchor(center)",
								translate: "-50% 0",
								marginBottom: 8,
								background: "#fff",
								padding: "6px 6px 24px",
								boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
								transform: `rotate(${m.rotate}deg)`,
								pointerEvents: "none",
								...fadeBlurStyle(m.id, useFadeBlur),
							}}
						>
							<Image
								src={m.image}
								alt={m.caption}
								loading="lazy"
								decoding="async"
								width={polaroidSize}
								height={polaroidSize}
								style={{
									display: "block",
									width: polaroidSize,
									height: polaroidSize,
									objectFit: "cover",
								}}
							/>
							<span
								style={{
									position: "absolute",
									bottom: 5,
									left: 0,
									right: 0,
									textAlign: "center",
									fontFamily: "system-ui, sans-serif",
									fontSize: "0.5rem",
									color: "#333",
								}}
							>
								{m.caption}
							</span>
						</div>
					);
				}
				if (m.kind === "sticker") {
					return (
						<div
							key={m.id}
							style={{
								position: "absolute",
								positionAnchor: `--cobe-${m.id}`,
								bottom: "anchor(top)",
								left: "anchor(center)",
								translate: "-50% 0",
								fontSize: tier === "low" ? "1.65rem" : "2rem",
								lineHeight: 1,
								transform: `rotate(${m.rotateDeg}deg)`,
								filter: stickerFilter,
								pointerEvents: "none",
								opacity: `var(--cobe-visible-${m.id}, 0)`,
								transition: "opacity 0.2s ease",
							}}
						>
							{m.sticker}
						</div>
					);
				}
				if (m.kind === "label") {
					return (
						<div
							key={m.id}
							style={{
								position: "absolute",
								positionAnchor: `--cobe-${m.id}`,
								bottom: "anchor(top)",
								left: "anchor(center)",
								translate: "-50% 0",
								marginBottom: -10,
								padding: "0.4rem 0.65rem 0.35rem",
								background: m.color,
								color: "#fff",
								fontFamily: "ui-rounded, system-ui, sans-serif",
								fontSize: tier === "low" ? "0.75rem" : "0.85rem",
								fontWeight: 600,
								whiteSpace: "nowrap",
								transform: `rotate(${m.rotate}deg)`,
								borderRadius: 4,
								boxShadow:
									tier === "low"
										? "0 1px 3px rgba(0,0,0,0.2)"
										: "0 1px 3px rgba(0,0,0,0.2), 0 3px 8px rgba(0,0,0,0.1)",
								pointerEvents: "none",
								overflow: "hidden",
								...fadeBlurStyle(m.id, useFadeBlur),
							}}
						>
							{tier !== "low" && (
								<span
									style={{
										position: "absolute",
										top: 0,
										left: 0,
										right: 0,
										height: "50%",
										background:
											"linear-gradient(180deg, rgba(255,255,255,0.35) 0%, transparent 100%)",
										pointerEvents: "none",
									}}
								/>
							)}
							{m.text}
						</div>
					);
				}
				return null;
			})}
		</>
	);
});

export interface GlobeMultiProps {
	markers?: GlobeMultiMarker[];
	arcs?: FlightArc[];
	className?: string;
	speed?: number;
	/** Rendering cost preset. `"auto"` picks from hardware hints (cores / deviceMemory when available). */
	quality?: GlobeMultiQuality;
}

export function GlobeMulti({
	markers: markersProp = GLOBE_MULTI_DEFAULT_MARKERS,
	arcs: arcsProp = GLOBE_MULTI_DEFAULT_ARCS,
	className = "",
	speed = 0.003,
	quality = "auto",
}: GlobeMultiProps) {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const pointerInteracting = useRef<{ x: number; y: number } | null>(null);
	const dragOffset = useRef({ phi: 0, theta: 0 });
	const phiOffsetRef = useRef(0);
	const thetaOffsetRef = useRef(0);
	const isPausedRef = useRef(false);
	const isOnScreenRef = useRef(true);
	const tabVisibleRef = useRef(
		typeof document === "undefined"
			? true
			: document.visibilityState === "visible",
	);

	const filterId = `globe-mo-${useId().replace(/:/g, "")}`;

	const [reduceMotion, setReduceMotion] = useState(false);

	useEffect(() => {
		const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
		const apply = () => {
			setReduceMotion(mq.matches);
		};
		apply();
		mq.addEventListener("change", apply);
		return () => mq.removeEventListener("change", apply);
	}, []);

	const tier = useMemo(() => {
		if (reduceMotion) return "low";
		return resolveTier(quality);
	}, [quality, reduceMotion]);

	const markers = useMemo(
		() => thinMarkers(markersProp, tier),
		[markersProp, tier],
	);
	const arcs = useMemo(() => thinArcs(arcsProp, tier), [arcsProp, tier]);

	const effectiveSpeed =
		tier === "low" ? speed * 0.6 : tier === "medium" ? speed * 0.85 : speed;

	const overlayTierProps: OverlayTierProps = { tier, reduceMotion, filterId };

	const handlePointerDown = useCallback((e: React.PointerEvent) => {
		pointerInteracting.current = { x: e.clientX, y: e.clientY };
		e.currentTarget.setPointerCapture(e.pointerId);
		if (canvasRef.current) canvasRef.current.style.cursor = "grabbing";
		isPausedRef.current = true;
	}, []);

	const handlePointerUp = useCallback((e: React.PointerEvent) => {
		if (pointerInteracting.current !== null) {
			phiOffsetRef.current += dragOffset.current.phi;
			thetaOffsetRef.current += dragOffset.current.theta;
			dragOffset.current = { phi: 0, theta: 0 };
		}
		pointerInteracting.current = null;
		if (canvasRef.current) canvasRef.current.style.cursor = "grab";
		isPausedRef.current = false;
		try {
			e.currentTarget.releasePointerCapture(e.pointerId);
		} catch {
			/* already released */
		}
	}, []);

	const handlePointerMove = useCallback((e: React.PointerEvent) => {
		if (pointerInteracting.current === null) return;
		dragOffset.current = {
			phi: (e.clientX - pointerInteracting.current.x) / 300,
			theta: (e.clientY - pointerInteracting.current.y) / 1000,
		};
	}, []);

	const cobeMarkers = useMemo(
		() =>
			markers.map((m) => ({
				location: m.location,
				size: cobeMarkerSize(m.kind),
				id: m.id,
			})),
		[markers],
	);

	const cobeArcs = useMemo(
		() => arcs.map((a) => ({ from: a.from, to: a.to, id: a.id })),
		[arcs],
	);

	const { mapSamples, maxDpr, frameMs } = cobeConfigForTier(tier);

	useEffect(() => {
		if (!canvasRef.current || !containerRef.current) return;
		const canvas = canvasRef.current;
		const container = containerRef.current;
		let globe: ReturnType<typeof createGlobe> | null = null;
		let raf = 0;
		let phi = 0;
		let lastFrame = 0;

		const syncClock = () => {
			tabVisibleRef.current = document.visibilityState === "visible";
			return isOnScreenRef.current && tabVisibleRef.current;
		};

		function stopTick() {
			if (raf !== 0) {
				cancelAnimationFrame(raf);
				raf = 0;
			}
		}

		function tick(now: number) {
			if (!globe) {
				raf = 0;
				return;
			}
			if (!syncClock()) {
				raf = 0;
				return;
			}
			raf = requestAnimationFrame(tick);
			if (now - lastFrame < frameMs) return;
			lastFrame = now;
			if (!isPausedRef.current) phi += effectiveSpeed;
			globe.update({
				phi: phi + phiOffsetRef.current + dragOffset.current.phi,
				theta: 0.2 + thetaOffsetRef.current + dragOffset.current.theta,
			});
		}

		function startTick() {
			if (raf !== 0 || !globe || !syncClock()) return;
			lastFrame = 0;
			raf = requestAnimationFrame(tick);
		}

		function bumpAnimation() {
			stopTick();
			if (globe && syncClock()) startTick();
		}

		const onVisibility = () => bumpAnimation();

		const io = new IntersectionObserver(
			([e]) => {
				isOnScreenRef.current = e?.isIntersecting ?? false;
				bumpAnimation();
			},
			{ rootMargin: "80px", threshold: 0 },
		);
		io.observe(container);
		document.addEventListener("visibilitychange", onVisibility);

		function init() {
			const width = canvas.offsetWidth;
			if (width === 0 || globe) return;

			globe = createGlobe(canvas, {
				devicePixelRatio: Math.min(window.devicePixelRatio || 1, maxDpr),
				width,
				height: width,
				phi: 0,
				theta: 0.2,
				dark: 0.04,
				diffuse: 1.5,
				mapSamples,
				mapBrightness: tier === "low" ? 8 : 9,
				baseColor: [0.98, 0.98, 1],
				markerColor: [0.4, 0.65, 0.9],
				glowColor: [0.94, 0.93, 0.91],
				markerElevation: tier === "low" ? 0.05 : 0.07,
				markers: cobeMarkers,
				arcs: cobeArcs,
				arcColor: [0.35, 0.6, 1],
				arcWidth: 0.5,
				arcHeight: 0.25,
				opacity: 0.72,
			});

			requestAnimationFrame(() => {
				canvas.style.opacity = "1";
			});
			bumpAnimation();
		}

		if (canvas.offsetWidth > 0) {
			init();
		} else {
			const ro = new ResizeObserver((entries) => {
				if (entries[0]?.contentRect.width > 0) {
					ro.disconnect();
					init();
				}
			});
			ro.observe(canvas);
		}

		return () => {
			document.removeEventListener("visibilitychange", onVisibility);
			io.disconnect();
			stopTick();
			if (globe) globe.destroy();
		};
	}, [
		cobeMarkers,
		cobeArcs,
		effectiveSpeed,
		mapSamples,
		maxDpr,
		frameMs,
		tier,
	]);

	return (
		<div
			ref={containerRef}
			className={`w-full relative aspect-square select-none ${className}`}
			style={{ contain: "layout paint size", contentVisibility: "auto" }}
		>
			<style>{`
        @keyframes globe-multi-weather-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
      `}</style>
			{tier === "high" && (
				<svg
					width="0"
					height="0"
					style={{ position: "absolute" }}
					aria-hidden="true"
				>
					<defs>
						<filter id={filterId}>
							<feMorphology
								in="SourceAlpha"
								result="Dilated"
								operator="dilate"
								radius="2"
							/>
							<feFlood floodColor="#ffffff" result="OutlineColor" />
							<feComposite
								in="OutlineColor"
								in2="Dilated"
								operator="in"
								result="Outline"
							/>
							<feMerge>
								<feMergeNode in="Outline" />
								<feMergeNode in="SourceGraphic" />
							</feMerge>
						</filter>
					</defs>
				</svg>
			)}
			<canvas
				ref={canvasRef}
				onPointerDown={handlePointerDown}
				onPointerUp={handlePointerUp}
				onPointerMove={handlePointerMove}
				onPointerCancel={handlePointerUp}
				style={{
					width: "100%",
					height: "100%",
					cursor: "grab",
					opacity: 0,
					transition: "opacity 1s ease",
					borderRadius: "50%",
					touchAction: "none",
					contain: "strict",
				}}
			/>
			<GlobeMultiArcOverlays arcs={arcs} {...overlayTierProps} />
			<GlobeMultiMarkerOverlays markers={markers} {...overlayTierProps} />
		</div>
	);
}
